/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import PropTypes from 'prop-types'
import React, {useEffect, useState} from 'react'

// Project Components
import TextImageCard from '../text-image-card'
// Components
import {
    Box,
    Flex,
    Modal,
    ModalBody,
    ModalContent,
    Skeleton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import NestedMobileMenu from '../nested-mobile-menu/index'

// Others
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

import {styleConfigChakraKeys} from '../../constants'
import LoginAction from '../login-action'
import MobileMenuItem from './partials/mobile-menu-item'
import {LanguageSelector} from '../language-selector/LanguageSelector'
import Link from '../link'
import {navLinkAnimation} from '../../theme/components/project/drawer-menu'
import {useMenuQuery} from '../../clients/contentful/hooks/use-menu-query.hook'

/**
 * This is the navigation component used for mobile devices (phone and tablet). It's
 * main usage is to navigate from one category to the next, but also homes links to
 * support, log in and out actions, as support links.
 */
const DrawerMenu = ({header, isOpen, onClose = noop, paddingTop = 40}) => {
    const {menuItems = []} = useMenuQuery()
    const styles = useMultiStyleConfig(styleConfigChakraKeys.drawerMenu)
    const [showLoading, setShowLoading] = useState(false)
    const [ariaBusy, setAriaBusy] = useState('true')
    const headerContent = header?.headerCollection?.items[0]
    const bannerData = headerContent?.bannerCollection?.items
    const rightData = header?.right
    const bookServices = rightData?.filter((_item, ind) => ind === 0)?.at(-1)

    useEffect(() => {
        setAriaBusy('false')
    }, [])

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="full"
            variant="megaNavModal"
        >
            <ModalContent
                {...styles.modalContent}
                paddingTop={`${paddingTop}px`}
            >
                {/* Main Content */}
                <ModalBody
                    {...styles.modalBody}
                    paddingBottom="0"
                    sx={{'.mobile-mega-nav': {display: 'flex'}}}
                >
                    <div
                        id="category-nav"
                        aria-live="polite"
                        aria-busy={ariaBusy}
                        aria-atomic="true"
                        className="mobile-mega-nav"
                    >
                        {showLoading && <LoadingSpinner />}

                        {/* Category Navigation */}
                        {header ? (
                            <Flex flexFlow="column" margin="auto" gap="30px">
                                <Box>
                                    <NestedMobileMenu
                                        isNavOpen={isOpen}
                                        items={menuItems}
                                        closeDrawer={onClose}
                                    />
                                    <Link
                                        {...bookServices}
                                        onLinkClick={() => {
                                            onClose()
                                        }}
                                        buttonStyles={{
                                            ...styles.mobileNavLink,
                                            backgroundColor: 'white',
                                            background: 'white',
                                            padding: '0px',
                                            animation: `${navLinkAnimation} 0.1s ease-in-out calc((var(--index) + 1) * 80ms) forwards`
                                        }}
                                        style={{'--index': 3}}
                                        marginLeft="auto"
                                        textAlign="right"
                                        sx={{
                                            '-webkit-tap-highlight-color':
                                                'transparent'
                                        }}
                                    />

                                    <LoginAction
                                        itemLength={menuItems.length}
                                        setShowLoading={setShowLoading}
                                    />
                                </Box>
                                <LanguageSelector />
                            </Flex>
                        ) : (
                            <Box width="80%" margin="auto">
                                <MobileMenuItem />
                                <MobileMenuItem />
                                <MobileMenuItem />
                                <MobileMenuItem />
                                <MobileMenuItem />
                                <MobileMenuItem />
                                <Flex marginTop="34px" gap="20px">
                                    <Skeleton
                                        mb="2"
                                        height="200px"
                                        width="50%"
                                    />
                                    <Skeleton
                                        mb="2"
                                        height="200px"
                                        width="50%"
                                    />
                                </Flex>
                            </Box>
                        )}
                    </div>

                    <Flex marginTop="34px">
                        {bannerData?.map((item, index) => {
                            return (
                                <TextImageCard
                                    key={index}
                                    img={{
                                        src: item?.media.desktop?.url,
                                        alt: item?.text
                                    }}
                                    text={item?.text}
                                    link={item.cta}
                                    textAlignment={styles.textAlignment}
                                    cardAlignment={styles.cardAlignment}
                                    onLinkClick={onClose}
                                />
                            )
                        })}
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

DrawerMenu.displayName = 'DrawerMenu'

DrawerMenu.propTypes = {
    header: PropTypes.object,
    /**
     * The opened state of the drawer.
     */
    isOpen: PropTypes.bool,
    /**
     * Function called when the drawer is dismissed.
     */
    onClose: PropTypes.func,
    /**
     * Function called when the drawer logo is clicked.
     */
    onLogoClick: PropTypes.func,
    paddingTop: PropTypes.number
}

export default DrawerMenu
